export const EXECUTE_JS_DEADLINE_MS = 110_000;

const NEW_FUNCTION_AND_CONSOLE_SHADOW_LINES_BEFORE_SCRIPT = 3;

const CONSOLE_SHADOW =
    "const console = { log: (...a) => logs.push(a.map(String).join(\" \")), " +
    "warn: (...a) => logs.push(a.map(String).join(\" \")), " +
    "error: (...a) => logs.push(a.map(String).join(\" \")), " +
    "info: (...a) => logs.push(a.map(String).join(\" \")) };\n";

const ANONYMOUS_FRAME_LINE = /<anonymous>:(\d+):\d+/;

export type AgentScript = (word: typeof Word, context: Word.RequestContext, logs: string[]) => Promise<unknown>;

export function compileAgentScript(code: string): AgentScript {
    return new Function("Word", "context", "logs", `return (async () => { ${CONSOLE_SHADOW}${code} })()`) as AgentScript;
}

export function describeScriptError(error: Error): string {
    if (error instanceof OfficeExtension.Error) {
        return JSON.stringify({
            code: error.code,
            message: error.message,
            debugInfo: error.debugInfo,
            traceMessages: error.traceMessages,
            innerError: error.innerError,
        });
    }
    const frame = ANONYMOUS_FRAME_LINE.exec(error.stack ?? "");
    if (frame === null) return error.message;
    const scriptLine = Number(frame[1]) - NEW_FUNCTION_AND_CONSOLE_SHADOW_LINES_BEFORE_SCRIPT;
    return `${error.message} at script line ${scriptLine}`;
}

export function runScriptWithinDeadline<T>(startScript: (deadline: AbortSignal) => Promise<T>): Promise<T> {
    const deadline = new AbortController();
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
            deadline.abort();
            reject(new Error(
                `The script did not finish within ${EXECUTE_JS_DEADLINE_MS / 1000} seconds. It may still be running and holds the document until it ends. ` +
                "Do not retry the mutation: make the next call a read that checks whether the change landed, then split the remaining work into smaller scripts.",
            ));
        }, EXECUTE_JS_DEADLINE_MS);
        startScript(deadline.signal).then(resolve, reject).finally(() => clearTimeout(timer));
    });
}
